import { t } from '@lingui/core/macro'
import { useMemo, useState } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import ChartInsight from '~/components/chart_insight'
import ECharts from '~/components/echarts'
import FootNote from '~/components/footnote'
import AWSEBSDashboardFilters from '~/dashboard/aws_ebs_dashboard_filters'
import AWSEBSDashboardInventoryFilters from '~/dashboard/aws_ebs_dashboard_inventory_filters'
import {
  awsEBSPreviousMonthSpendChartPath,
  awsEBSQuantityByVolumeKindChartPath,
  awsEBSQuantityByVolumeStateChartPath,
  awsEBSVolumesWithEC2InstancesChartPath,
  awsMonthlyEBSSnapshotSpendQueryChartPath,
  awsMonthlyEBSVolumeSpendByVolumeTypeChartPath,
  awsPreviousMonthEBSSnapshotSpendChartPath,
  awsPreviousMonthEBSSpendByUsageTypeGroupChartPath,
} from '~/routes'

export default function AWSEBSDashboardCharts({ organizationId: orgId }) {
  const [mainParams, setMainParams] = useState({})
  const [inventoryParams, setInventoryParams] = useState({})

  const [openedTabs, setOpenedTabs] = useState(['main-tab'])
  const [activeTab, setActiveTab] = useState('main-tab')

  const handleTabChange = (tabName) => {
    setActiveTab(tabName)

    // Lazy load charts
    if (!openedTabs.includes(tabName)) {
      setOpenedTabs((prevOpenedTabs) => [...prevOpenedTabs, tabName])
    }
  }

  const openedTab = (tabName) => {
    return openedTabs.includes(tabName)
  }

  // memoize URLs to prevent rerenders
  const awsEBSPreviousMonthSpendChartUrl = useMemo(() => awsEBSPreviousMonthSpendChartPath(orgId), [orgId])
  const awsPreviousMonthEBSSpendByUsageTypeGroupChartUrl = useMemo(
    () => awsPreviousMonthEBSSpendByUsageTypeGroupChartPath(orgId),
    [orgId],
  )
  const awsPreviousMonthEBSSnapshotSpendChartUrl = useMemo(
    () => awsPreviousMonthEBSSnapshotSpendChartPath(orgId),
    [orgId],
  )
  const awsMonthlyEBSVolumeSpendByVolumeTypeChartUrl = useMemo(
    () => awsMonthlyEBSVolumeSpendByVolumeTypeChartPath(orgId),
    [orgId],
  )
  const awsMonthlyEBSSnapshotSpendQueryChartUrl = useMemo(
    () => awsMonthlyEBSSnapshotSpendQueryChartPath(orgId),
    [orgId],
  )
  const awsEBSVolumesWithEC2InstancesChartUrl = useMemo(() => awsEBSVolumesWithEC2InstancesChartPath(orgId), [orgId])
  const awsEBSQuantityByVolumeStateChartUrl = useMemo(() => awsEBSQuantityByVolumeStateChartPath(orgId), [orgId])
  const awsEBSQuantityByVolumeKindChartUrl = useMemo(() => awsEBSQuantityByVolumeKindChartPath(orgId), [orgId])

  return (
    <div className='card-tabs'>
      <Tabs id='controlled-tabs' activeKey={activeTab} onSelect={(k) => handleTabChange(k)}>
        <Tab eventKey='main-tab' title={t`Main Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSEBSDashboardFilters organizationId={orgId} setChartParams={setMainParams} />

                {openedTab('main-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ChartInsight
                        title={t`EBS Storage Spend`}
                        subtitle='Showing Past Month'
                        url={awsEBSPreviousMonthSpendChartUrl}
                        params={mainParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ChartInsight
                        title={t`EBS Snapshot Spend`}
                        subtitle='Showing Past Month'
                        url={awsPreviousMonthEBSSnapshotSpendChartUrl}
                        params={mainParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous Month EBS Disk Spend by Volume Type`}
                        chartInfoTitle='aws_ebs_previous_month_disk_spend_by_volume_type'
                        url={awsPreviousMonthEBSSpendByUsageTypeGroupChartUrl}
                        params={mainParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 18 Months EBS Storage by Usage Type Group`}
                        chartInfoTitle='aws_ebs_previous_18_months_storage_by_usage_type_group'
                        url={awsMonthlyEBSVolumeSpendByVolumeTypeChartUrl}
                        params={mainParams}
                        showExportButton
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 12 Months Snapshot Spend`}
                        chartInfoTitle='aws_ebs_previous_12_months_snapshot_spend'
                        url={awsMonthlyEBSSnapshotSpendQueryChartUrl}
                        params={mainParams}
                        showExportButton
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Total Space Allocated by Volume Type`}
                        chartInfoTitle='aws_ebs_total_space_allocated_by_volume_type'
                        url={awsEBSQuantityByVolumeKindChartUrl}
                        params={mainParams}
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
        <Tab eventKey='inventory-tab' title={t`Inventory`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSEBSDashboardInventoryFilters organizationId={orgId} setChartParams={setInventoryParams} />

                {openedTab('inventory-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Quantity Breakdown of EBS Volumes Status`}
                        chartInfoTitle='aws_ebs_quantity_breakdown_by_volume_status'
                        url={awsEBSVolumesWithEC2InstancesChartUrl}
                        params={inventoryParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Quantity of EBS Volumes By State`}
                        chartInfoTitle='aws_ebs_quantity_by_volume_state'
                        url={awsEBSQuantityByVolumeStateChartUrl}
                        params={inventoryParams}
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
  )
}
